import { useEffect, useState } from 'react';
import {
  CARD_COLOUR_PRESETS,
  cardColourFromHex,
  cardColourStore,
  cardColourToHex,
} from '../game/card-colour';

/**
 * **A paleta dos efeitos de carta** (gate V28H.O).
 *
 * ## Onde está, e porquê
 *
 * Encostada à barra das cartas, e não numa página de definições: a coisa que ela
 * pinta está a três centímetros dela, e quem quer mudar a cor está a olhar para
 * o efeito quando decide. Uma definição escondida obrigaria a sair do jogo para
 * ver o resultado de a mudar.
 *
 * ## Oito atalhos e um selector livre
 *
 * A paleta é o caminho de um clique; o `input[type=color]` é o que impede a
 * paleta de ser um tecto. As texturas são brancas e recebem uma tinta, portanto
 * qualquer cor é tão barata como as oito.
 *
 * ## Ela não desenha nada
 *
 * Escreve na caixa, e a cena lê a caixa no quadro seguinte. Nem este componente
 * conhece Phaser, nem a cena conhece React - é a mesma fronteira que a ponte
 * defende em todo o lado.
 */
export function CardColourControl({
  store = cardColourStore,
}: {
  readonly store?: typeof cardColourStore;
}) {
  const [colour, setColour] = useState(() => store.current());
  const [open, setOpen] = useState(false);

  /*
   * A caixa pode mudar sem ser por aqui - outro painel, uma sessão restaurada -
   * e o botão tem de contar a verdade. Subscrever é o que o mantém honesto.
   */
  useEffect(() => store.subscribe(setColour), [store]);

  const choose = (value: number): void => {
    store.set(value);
    setColour(store.current());
  };

  return (
    <div className="card-colour" data-testid="card-colour">
      <button
        type="button"
        className="card-colour-toggle"
        data-testid="card-colour-toggle"
        aria-expanded={open}
        aria-label="Cor dos efeitos de carta"
        title="Cor dos efeitos de carta"
        onClick={() => setOpen((was) => !was)}
      >
        <span
          className="card-colour-swatch"
          data-testid="card-colour-current"
          data-colour={cardColourToHex(colour)}
          style={{ backgroundColor: cardColourToHex(colour) }}
        />
      </button>
      {/*
        `section` com nome acessível, e não `role="group"`: é a mesma escolha
        que a barra das cartas já faz - um grupo nomeado sem inventar um
        `fieldset`, que é de formulários.
      */}
      {open ? (
        <section
          className="card-colour-palette"
          data-testid="card-colour-palette"
          aria-label="Cores"
        >
          {CARD_COLOUR_PRESETS.map((preset) => (
            <button
              key={preset.name}
              type="button"
              className="card-colour-option"
              data-testid={`card-colour-${preset.name.toLowerCase()}`}
              aria-label={preset.name}
              title={preset.name}
              aria-pressed={preset.value === colour}
              style={{ backgroundColor: cardColourToHex(preset.value) }}
              onClick={() => choose(preset.value)}
            />
          ))}
          {/*
            O selector nativo, e não uma roda desenhada à mão: o browser já tem
            um, o sistema operativo já o sabe apresentar, e escrever outro seria
            escrever acessibilidade que já existe.
          */}
          <label className="card-colour-free">
            <span className="card-colour-free-label">Outra</span>
            <input
              type="color"
              data-testid="card-colour-free"
              value={cardColourToHex(colour)}
              onChange={(event) => choose(cardColourFromHex(event.target.value))}
            />
          </label>
        </section>
      ) : null}
    </div>
  );
}
